import { useCallback, useMemo } from 'react';
import useInertiaData from './useInertiaData';

type GroupName = 'Líder Comercial' | 'Comercial' | 'Onboarding' | 'Estudiante' | 'Profesor' | 'Staff';

export const usePermissions = () => {
    const { user, can_import_users } = useInertiaData();

    const hasGroup = useCallback(
        (groupName: GroupName): boolean => {
            if (!user) return false;
            return user.groups?.some((group) => group.name === groupName) || false;
        },
        [user?.groups]
    );

    const hasAnyGroup = useCallback(
        (groupNames: GroupName[]): boolean => {
            if (!user) return false;
            return user.groups?.some((group) => groupNames.includes(group.name as GroupName)) || false;
        },
        [user?.groups]
    );

    const isSuperuser = useMemo(() => {
        return user?.is_superuser || false;
    }, [user?.is_superuser]);

    const isStaff = useMemo(() => {
        return user?.is_staff || false;
    }, [user?.is_staff]);

    const canAccessStaffPanel = useMemo(() => {
        if (!user) return false;
        return (
            user.is_superuser ||
            user.groups?.some(
                (group) =>
                    group.name === 'Líder Comercial' ||
                    group.name === 'Comercial' ||
                    group.name === 'Onboarding' ||
                    group.name === 'Profesor'
            ) ||
            false
        );
    }, [user?.is_superuser, user?.groups]);

    const canManageCommercialUsers = useMemo(() => {
        if (!user) return false;
        return (
            user.is_superuser ||
            user.groups?.some((group) => group.name === 'Líder Comercial') ||
            false
        );
    }, [user?.is_superuser, user?.groups]);

    const canImportUsers = useMemo(() => {
        if (!user) return false;
        return user.is_superuser || can_import_users || false;
    }, [user?.is_superuser, can_import_users]);

    return {
        hasGroup,
        hasAnyGroup,
        isSuperuser,
        isStaff,
        canAccessStaffPanel,
        canManageCommercialUsers,
        canImportUsers,
    };
};

export default usePermissions;
